/**
 * CombatVfx — world-space combat effects for battles: muzzle flashes,
 * bolts (tracers) from shooter to target, impact bursts, explosions with a
 * shockwave ring and lingering smoke, heal puffs. A port of SpaceModule's
 * fxImpact anatomy (hot core flash → thrown sparks → smoke) at human scale.
 * Bounded: at most FX_CAP live sprites; extra calls are dropped, never queued.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { glowTexture, ringTexture, spriteOf, disposeSprite, isShared } from './fx_textures.js';
import { disposeLater } from '../../lib/gpu_dispose.js';

const FX_CAP = 160;
const UP = new THREE.Vector3(0, 1, 0);

interface Fx { s: THREE.Sprite | THREE.Mesh; vel: THREE.Vector3 | null; ttl: number; life: number; grow: number; from: number; gravity: number; len?: number }

/** One unit cylinder for every bolt (scaled per shot) and one material per tint — never a fresh pipeline per shot. */
const ROD_GEO = new THREE.CylinderGeometry(1, 1, 1, 5, 1, true);
const rodMaterials = new Map<number, THREE.MeshBasicMaterial>();
function rodMaterial(tint: number): THREE.MeshBasicMaterial {
  let m = rodMaterials.get(tint);
  if (!m) { m = new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false }); m.userData.shared = true; markMrtOverlay(m); rodMaterials.set(tint, m); }
  return m;
}

export class CombatVfx {
  readonly root = new THREE.Group();
  private live: Fx[] = [];
  readonly stats = { flashes: 0, bolts: 0, impacts: 0, explosions: 0, heals: 0, dropped: 0 };
  private readonly _d = new THREE.Vector3();
  private readonly _q = new THREE.Quaternion();

  private push(fx: Fx): boolean {
    if (this.live.length >= FX_CAP) { this.stats.dropped++; disposeAny(fx.s); return false; }
    this.live.push(fx);
    return true;
  }

  /** The shot leaves the gun: a bright core + a puff. */
  muzzle(p: THREE.Vector3, tint = 0xffe6a8): void {
    this.stats.flashes++;
    const flash = spriteOf(glowTexture(), tint, 0.55);
    flash.position.copy(p); this.root.add(flash);
    this.push({ s: flash, vel: null, ttl: 0.07, life: 0.07, grow: 0.9, from: 0.55, gravity: 0 });
    const puff = spriteOf(glowTexture(), 0x8f8a80, 0.25, false, 0.35);
    puff.position.copy(p); this.root.add(puff);
    this.push({ s: puff, vel: new THREE.Vector3(0, 0.6, 0), ttl: 0.5, life: 0.5, grow: 0.9, from: 0.25, gravity: 0 });
  }

  /** A bolt streaks shooter → target over ~120 ms: a thin glowing rod + a moving head. */
  bolt(from: THREE.Vector3, to: THREE.Vector3, tint = 0x9fe8ff): void {
    this.stats.bolts++;
    const len = from.distanceTo(to);
    if (len < 0.2) return;
    const rod = new THREE.Mesh(ROD_GEO, rodMaterial(tint));
    rod.scale.set(0.025, len, 0.025);
    rod.position.copy(from).add(to).multiplyScalar(0.5);
    this._d.copy(to).sub(from).normalize();
    rod.quaternion.setFromUnitVectors(UP, this._d);
    this.root.add(rod);
    this.push({ s: rod, vel: null, ttl: 0.14, life: 0.14, grow: 1, from: 1, gravity: 0, len });
    const head = spriteOf(glowTexture(), tint, 0.38);
    head.position.copy(from); this.root.add(head);
    this.push({ s: head, vel: this._d.clone().multiplyScalar(len / 0.12), ttl: 0.12, life: 0.12, grow: 0.3, from: 0.38, gravity: 0 });
  }

  /** Projectile impact: core flash, thrown sparks, a smoke puff (scale ≈ metres). */
  impact(p: THREE.Vector3, scale: number, tint: number): void {
    this.stats.impacts++;
    const flash = spriteOf(glowTexture(), 0xffffff, scale * 1.6);
    flash.position.copy(p); this.root.add(flash);
    this.push({ s: flash, vel: null, ttl: 0.09, life: 0.09, grow: scale * 2.2, from: scale * 1.6, gravity: 0 });
    const n = Math.min(10, 4 + Math.round(scale * 3));
    for (let i = 0; i < n; i++) {
      const sp = spriteOf(glowTexture(), tint, scale * 0.3);
      sp.position.copy(p); this.root.add(sp);
      const v = new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.8, Math.random() - 0.5).normalize().multiplyScalar(scale * (4 + Math.random() * 6));
      this.push({ s: sp, vel: v, ttl: 0.35 + Math.random() * 0.25, life: 0.6, grow: scale * 0.05, from: scale * 0.3, gravity: 9 });
    }
    const smoke = spriteOf(glowTexture(), 0x3a3632, scale * 0.9, false, 0.5);
    smoke.position.copy(p); this.root.add(smoke);
    this.push({ s: smoke, vel: new THREE.Vector3(0, 0.8, 0), ttl: 0.9, life: 0.9, grow: scale * 2.6, from: scale * 0.9, gravity: 0 });
  }

  /** Heavy hit: a bigger burst, a shockwave ring on the ground, three smoke columns. */
  explosion(p: THREE.Vector3, scale: number, tint = 0xffb070): void {
    this.stats.explosions++;
    this.impact(p, scale * 1.6, tint);
    const r = spriteOf(ringTexture(), 0xfff1d0, scale * 0.8);
    r.position.copy(p); this.root.add(r);
    this.push({ s: r, vel: null, ttl: 0.45, life: 0.45, grow: scale * 7, from: scale * 0.8, gravity: 0 });
    for (let i = 0; i < 3; i++) {
      const sm = spriteOf(glowTexture(), 0x2c2826, scale * 0.8, false, 0.55);
      sm.position.copy(p).add(new THREE.Vector3((Math.random() - 0.5) * scale, 0.2, (Math.random() - 0.5) * scale));
      this.root.add(sm);
      this.push({ s: sm, vel: new THREE.Vector3((Math.random() - 0.5) * 0.6, 1.2 + Math.random(), (Math.random() - 0.5) * 0.6), ttl: 1.6 + Math.random() * 0.8, life: 2.2, grow: scale * 3.5, from: scale * 0.8, gravity: 0 });
    }
    const fire = spriteOf(glowTexture(), tint, scale * 1.2);
    fire.position.copy(p); this.root.add(fire);
    this.push({ s: fire, vel: new THREE.Vector3(0, 1.5, 0), ttl: 0.4, life: 0.4, grow: scale * 2.4, from: scale * 1.2, gravity: 0 });
  }

  /** A medic's touch: a soft green bloom rising off the patient. */
  heal(p: THREE.Vector3): void {
    this.stats.heals++;
    for (let i = 0; i < 4; i++) {
      const sp = spriteOf(glowTexture(), 0x8cf7a0, 0.28);
      sp.position.copy(p).add(new THREE.Vector3((Math.random() - 0.5) * 0.6, Math.random() * 0.4, (Math.random() - 0.5) * 0.6));
      this.root.add(sp);
      this.push({ s: sp, vel: new THREE.Vector3(0, 0.9 + Math.random() * 0.5, 0), ttl: 0.7, life: 0.7, grow: 0.05, from: 0.28, gravity: 0 });
    }
  }

  /** Melee: a sharp spark at the point of the blow. */
  clash(p: THREE.Vector3, tint = 0xfff3c0): void {
    this.stats.impacts++;
    for (let i = 0; i < 5; i++) {
      const sp = spriteOf(glowTexture(), tint, 0.22);
      sp.position.copy(p); this.root.add(sp);
      const v = new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.6, Math.random() - 0.5).normalize().multiplyScalar(2.5 + Math.random() * 2);
      this.push({ s: sp, vel: v, ttl: 0.25 + Math.random() * 0.15, life: 0.4, grow: 0.02, from: 0.22, gravity: 9 });
    }
  }

  update(dt: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const f = this.live[i];
      f.ttl -= dt;
      if (f.ttl <= 0) { disposeAny(f.s); this.live.splice(i, 1); continue; }
      const k = 1 - f.ttl / f.life;
      if (f.vel) {
        if (f.gravity) f.vel.y -= f.gravity * dt;
        f.s.position.addScaledVector(f.vel, dt);
      }
      // Shared materials: every fade is a SCALE curve (grow, then collapse at the end of life).
      if ((f.s as THREE.Sprite).isSprite) {
        const fade = k < 0.7 ? 1 : Math.max(0.01, 1 - (k - 0.7) / 0.3);
        f.s.scale.setScalar((f.from + (f.grow - f.from) * k) * fade);
      } else f.s.scale.set(0.025 * (1 - k), f.len ?? 1, 0.025 * (1 - k));
    }
  }

  get count(): number { return this.live.length; }

  dispose(): void {
    for (const f of this.live) disposeAny(f.s);
    this.live = [];
    this.root.parent?.remove(this.root);
  }
}

function disposeAny(o: THREE.Sprite | THREE.Mesh): void {
  if ((o as THREE.Sprite).isSprite) { disposeSprite(o as THREE.Sprite); return; }
  o.parent?.remove(o);
  const m = o as THREE.Mesh;
  if (m.geometry !== ROD_GEO) disposeLater(m.geometry);
  if (!isShared(m.material as THREE.Material)) disposeLater(m.material as THREE.Material);
}
